import React from 'react';
import { useNavigate } from 'react-router-dom';
import { openExternalUrl, isExternalLink, handleLinkClick } from '../lib/url-handler';

interface LinkHandlerProps {
  href: string;
  children: React.ReactNode;
  className?: string;
  target?: '_blank' | '_self' | '_parent' | '_top';
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  title?: string;
  rel?: string;
  style?: React.CSSProperties;
  ariaLabel?: string;
}

/**
 * Componente para lidar com links internos e externos de forma consistente
 */
export default function LinkHandler({
  href,
  children,
  className,
  target = '_blank',
  onClick,
  title,
  rel,
  style,
  ariaLabel
}: LinkHandlerProps) {
  const navigate = useNavigate();
  
  const handleClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Permite que o manipulador de eventos personalizado seja executado primeiro
    if (onClick) {
      onClick(e);
    }
    
    // Se o evento padrão foi prevenido pelo manipulador personalizado, respeitamos isso
    if (e.defaultPrevented) {
      return;
    }
    
    // Previne o comportamento padrão para que possamos lidar com a navegação
    e.preventDefault();
    
    // Determina se é um link interno ou externo e lida adequadamente
    await handleLinkClick(href, navigate, target);
  };
  
  // Determina se é um link externo para definir atributos de segurança
  const isExternal = isExternalLink(href);
  
  // Define rel para links externos por segurança
  const safeRel = isExternal 
    ? rel || 'noopener noreferrer' 
    : rel;
  
  return (
    <a
      href={href}
      className={className}
      target={target}
      onClick={handleClick}
      title={title}
      rel={safeRel}
      style={style}
      aria-label={ariaLabel}
    >
      {children}
    </a>
  );
}

/**
 * Hook personalizado para abrir links
 */
export function useOpenLink() {
  const navigate = useNavigate();
  
  const openLink = React.useCallback(
    async (url: string, target: string = '_blank') => {
      return await handleLinkClick(url, navigate, target);
    },
    [navigate]
  );
  
  return openLink;
}